import {t} from 'i18next';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import {Box, Grid, InputAdornment, Typography} from '@mui/material';
import ArrTreeView from '../../../common/arr-tree-view/ArrTreeView';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {Search} from '@mui/icons-material';
import {useGetBulkTagList} from '../../../../hooks/assignments/useAssignments';
import {transformJsTreeApiToTreeData} from '../../actionMenuUtils';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../shared/interfaces';
import Clear from '@mui/icons-material/CancelOutlined';

interface IExistingFolderProps {
  selectedExistingFolders: string[];
  setSelectedExistingFolders: (value: string[] | null | undefined) => void;
  searchInputExistingFolders: string;
  setSearchInputExistingFolders: (value: string) => void;
  setFolderTreeData?: (data: TreeViewBaseItem<ExtendedTreeItemProps>[]) => void;
}

// Helper function to get expanded ids based on the search keyword
const getExpandedIdsForSearch = (
  nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
  keyword: string
): string[] => {
  const ids = new Set<string>();
  const text = keyword.toLowerCase();

  const dfs = (node: TreeViewBaseItem<ExtendedTreeItemProps>) => {
    const isMatch = node.label?.toLowerCase().includes(text);
    let childMatch = false;

    if (node.children?.length) {
      for (const child of node.children) {
        if (dfs(child)) childMatch = true;
      }
    }

    if (isMatch || childMatch) {
      ids.add(node.id);
      return true;
    }

    return false;
  };

  nodes.forEach((root) => dfs(root));
  return Array.from(ids);
};

const ExistingFolder: React.FC<IExistingFolderProps> = ({
  selectedExistingFolders,
  setSelectedExistingFolders,
  searchInputExistingFolders,
  setSearchInputExistingFolders,
  setFolderTreeData,
}) => {
  const enBulkFolder = 'actionMenu.bulkFolderFlyout';
  const [searchInput, setSearchInput] = useState<string>(
    searchInputExistingFolders
  );
  const [treeKey, setTreeKey] = useState<number>(0);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const {data: BulkFolderData, isPending: isLoading} =
    useGetBulkTagList('BulkFolder');
  const getFirstLevelIds = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ): string[] => {
    return nodes.map((node) => node.id);
  };
  const treeData = useMemo(() => {
    return transformJsTreeApiToTreeData(BulkFolderData);
  }, [BulkFolderData]);

  useEffect(() => {
    if (!treeData || treeData.length === 0) return;

    const firstLevelIds = getFirstLevelIds(treeData);
    setExpandedIds(firstLevelIds); // expand only first layer
    setTreeKey((prev) => prev + 1);
  }, [treeData]);

  useEffect(() => {
    const expanded = getExpandedIdsForSearch(
      treeData,
      searchInputExistingFolders
    );
    setExpandedIds(expanded);
    setTreeKey((prev) => prev + 1); // force remount to apply expandedIds
  }, [searchInputExistingFolders, treeData]);

  useEffect(() => {
    if (treeData?.length && setFolderTreeData) {
      setFolderTreeData(treeData);
    }
  }, [treeData, setFolderTreeData]);

  const handleSearchKeyEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      setSearchInputExistingFolders(searchInput.trim());
    }
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchInputExistingFolders('');
    setExpandedIds(getFirstLevelIds(treeData));
    setTreeKey((prev) => prev + 1); // remount tree to apply
  };

  const handleSelected = useCallback(
    (values: string[] | null) => {
      // Delay state update so it happens AFTER render, avoiding the error
      setTimeout(() => {
        setSelectedExistingFolders(values);
      }, 0);
    },
    [setSelectedExistingFolders]
  );

  return (
    <>
      <Box className="search-box">
        <Grid container columnSpacing={2}>
          <Grid size={{xs: 12, sm: 6}}>
            <ArrTextField
              state="enabled"
              sizeVariant="medium"
              className="search-input"
              placeholder={t(`${enBulkFolder}.searchPlaceholder`)}
              aria-label={t(`${enBulkFolder}.searchPlaceholder`)}
              dataTestId="existing-folder-search-input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyEnter}
              slotProps={{
                inputLabel: {shrink: false},
                input: {
                  startAdornment: (
                    <InputAdornment
                      position="end"
                      className="search-adornment-icon"
                    >
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput ? (
                    <InputAdornment
                      position="end"
                      className="clear-adornment-icon"
                    >
                      <Clear
                        onClick={handleClearSearch}
                        style={{cursor: 'pointer'}}
                        data-testid="search-clear-btn"
                      />
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>
      <Box className="tree-heading-box">
        <Typography
          className="typo-text subhead4_size"
          data-testid="select-existing-folders"
          tabIndex={0}
        >
          {t(`${enBulkFolder}.selectExistingFolders`)}
        </Typography>
      </Box>

      <Box
        className="folder-tree-box  existing-tree"
        data-testid="existing-folder-tree"
      >
        {isLoading || !treeData.length ? (
          <ArrLoadingSpinner />
        ) : (
          <ArrTreeView
            key={treeKey}
            itemsList={treeData}
            selectedItem={''}
            handleNoResultMessage={t(`${enBulkFolder}.noResult`)}
            allowGlobalSelect={true}
            treeSelectedItems={selectedExistingFolders}
            multipleSelection={true}
            isShowCountLabel={false}
            isShowCheckbox={true}
            showActions={false}
            customContentHeight={36}
            defaultExpandedIds={selectedExistingFolders}
            setSelectedItem={handleSelected}
            searchKeyword={searchInputExistingFolders}
            setExpandedFolders={setExpandedIds}
          />
        )}
      </Box>
    </>
  );
};

export default ExistingFolder;
